import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { makePersonSender } from '@/components/account/record/factWriters.js';

// "MAKE A PERSON" FOR A CONTACT-LESS PILOT (people-create-door C8, AR12, ruling 1:
// every person gets an edit in TRACON). A pilot whose form names no CRM contact is
// not a person yet, so there is nothing to edit; this sheet makes one through the
// existing fix-pilot door (broker only): a CRM contact (and at "both" its TRACON
// person) from the name typed here, the form relinked to it. The name is
// required; the email and phone are optional. Prefilled from the form's own
// parts (never a split name). Posts only through factWriters makePersonSender
// (one client path); a refusal (a placeholder name, a malformed email) shows the
// server's words. The host re-reads on success, and the pilot's name then opens
// the person sheet. Guard: tests/person-edit-surfaces.test.js M1, M2.
const FIELDS = [['first_name', 'First name'], ['last_name', 'Last name'], ['email', 'Email (optional)'], ['phone', 'Phone (optional)']];
const seed = (p) => Object.fromEntries(FIELDS.map(([k]) => [k, (p && p[k]) || '']));

export default function MakePersonSheet({ open, onOpenChange, target, prefill, pilotName, onMade }) {
  const send = makePersonSender({ door: 'fix_pilot', key: 'make_person', target });
  const [draft, setDraft] = useState(() => seed(prefill));
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setDraft(seed(prefill)); }, [open, target?.phf_token]); // eslint-disable-line react-hooks/exhaustive-deps
  const typed = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, String(v || '').trim()]));
  const ready = !!(send && typed.first_name && typed.last_name);

  async function save() {
    if (!ready || saving) return;
    const body = { first_name: typed.first_name, last_name: typed.last_name };
    if (typed.email) body.email = typed.email;
    if (typed.phone) body.phone = typed.phone;
    setSaving(true);
    try {
      const r = await send(body);
      toast.success(`${typed.first_name} ${typed.last_name} is a person now`);
      onMade?.(r?.contact_id || null);
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err?.body?.error || err?.message || 'Could not make the person');
    } finally {
      setSaving(false);
    }
  }

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="md:max-w-md">
        <SheetHeader>
          <SheetTitle>Make a person</SheetTitle>
          <SheetDescription>
            {pilotName || 'This pilot'} has no CRM contact yet. This makes one from the name below and links the pilot's form to it. Their name then opens the person sheet.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-3" data-make-person-form>
          {FIELDS.map(([k, l], i) => (
            <label key={k} className="block space-y-1 text-xs text-text-muted">
              <span>{l}</span>
              <Input name={k} value={draft[k]} onChange={set(k)} aria-label={l} type={k === 'email' ? 'email' : k === 'phone' ? 'tel' : 'text'} disabled={saving} autoFocus={i === 0} />
            </label>
          ))}
          {!send ? <p className="text-xs text-text-dim" data-make-person="no-target">This pilot's form cannot be reached from here.</p> : null}
        </div>
        <SheetFooter className="mt-6">
          <Button type="button" variant="ghost" onClick={() => onOpenChange?.(false)} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={save} disabled={!ready || saving}>{saving ? 'Making...' : 'Make a person'}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
